// React
// Date
import { format, parseISO, addDays, startOfDay, isBefore, isValid } from "date-fns";


// Hooks
import useIsMountedRef from '../hooks/useIsMountedRef';
import useTabs from '../hooks/useTabs';

// Components
import Page from '../components/Page';
import HeaderBreadcrumbs from '../components/HeaderBreadcrumbs';
import Iconify from '../components/Iconify';

// Utils
import useConfiguration from '../hooks/useConfiguration';
import { Box, Button, Checkbox, Container, Toolbar } from '@mui/material';
import { APP_PATH } from 'src/routes/paths';
import axiosInstance from 'src/utils/axios';
import { API } from 'src/utils/api';
import { addDaysToDateString, datePlusDays } from 'src/utils/dateHelper';
import { useEffect, useMemo, useState } from 'react';
import { useBulkArchiveInstruments, useListInstruments } from 'src/api';

import {
  Grid,
  Paper,
  Typography,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
} from "@mui/material";
import SimpleBar from "simplebar-react";
import { useSnackbar } from "notistack";
import { useNavigate } from "react-router";
// ----------------------------------------------------------------------

const bucketInstruments = (instruments = []) => {
  const today = startOfDay(new Date());
  const week = addDays(today, 7);
  const month = addDays(today, 30);
  const threeMonths = addDays(today, 90);

  const buckets = {
    overdue: [],
    week: [],
    month: [],
    three: [],
  };

  // --- FIRST: put items into buckets ---
  instruments.forEach((inst) => {
    const raw = inst.next_calibration;
    if (!raw) return;

    const d = parseISO(raw);
    if (!isValid(d)) return;

    if (isBefore(d, today)) {
      buckets.overdue.push(inst);
    } else if (isBefore(d, week)) {
      buckets.week.push(inst);
    } else if (isBefore(d, month)) {
      buckets.month.push(inst);
    } else if (isBefore(d, threeMonths) || +d === +threeMonths) {
      buckets.three.push(inst);
    }
  });

  // --- SECOND: sorting function ---
  const sortFn = (a, b) => {
    const da = parseISO(a.next_calibration);
    const db = parseISO(b.next_calibration);

    if (isValid(da) && isValid(db)) {
      if (da < db) return -1;
      if (da > db) return 1;
    }

    const siteA = a.tag?.location?.site?.name || "";
    const siteB = b.tag?.location?.site?.name || "";
    return siteA.localeCompare(siteB);
  };

  // --- THIRD: sort each bucket ---
  buckets.overdue.sort(sortFn);
  buckets.week.sort(sortFn);
  buckets.month.sort(sortFn);
  buckets.three.sort(sortFn);

  return buckets;
};

// const bucketInstruments = (instruments = []) => {
//   const today = startOfDay(new Date());
//   const week = addDays(today, 7);
//   const month = addDays(today, 30);
//   const threeMonths = addDays(today, 90);
//
//   const buckets = {
//     overdue: [],
//     week: [],
//     month: [],
//     three: [],
//   };
//
//   instruments.forEach((inst) => {
//     if (!inst.next_calibration) return;
//
//     const d = parseISO(inst.next_calibration);
//     if (!isValid(d)) return;
//
//     if (isBefore(d, today)) {
//       buckets.overdue.push(inst);
//     } else if (isBefore(d, week)) {
//       buckets.week.push(inst);
//     } else if (isBefore(d, month)) {
//       buckets.month.push(inst);
//     } else if (isBefore(d, threeMonths) || +d === +threeMonths) {
//       buckets.three.push(inst);
//     }
//   });
//
//   return buckets;
// };
//
const COLUMNS = [
  { id: "select", label: "", width: "6%" },
  { id: "tag", label: "Tag", width: "14%" },
  { id: "client", label: "Client", width: "20%" },
  { id: "site", label: "Site", width: "40%" },
  { id: "date", label: "Due", width: "20%" },
];

const dataCellSx = {
  whiteSpace: "nowrap",
  overflow: "hidden",
  textOverflow: "ellipsis",
};

const tagCellClickableSx = {
  whiteSpace: "nowrap",
  overflow: "hidden",
  textOverflow: "ellipsis",
  color: "#1976d2",
  cursor: "pointer",
  "&:hover": {
    textDecoration: "underline",
  },
};

const DueTable = ({ title, items, selectedIds, onToggleOne, onToggleMany, onArchive }) => {
  const ids = items.map((inst) => inst.id);
  const selectedInBucketIds = ids.filter((id) => selectedIds.includes(id));

  const allSelected = ids.length > 0 && selectedInBucketIds.length === ids.length;
  const someSelected = selectedInBucketIds.length > 0 && !allSelected;

  const navigate = useNavigate()

  const handleHeaderCheckboxChange = () => {
    onToggleMany(ids);
  };

  const handleArchiveClick = () => {
    onArchive(selectedInBucketIds);
  };

  return (
    <Paper
      sx={{
        p: 2,
        height: 450,
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
      }}
    >
      {/* Header row with title + Archive button */}
      <Box sx={{ display: "flex", alignItems: "center", mb: 1, gap: 1 }}>
        <Typography variant="h6" sx={{ flexGrow: 1 }}>
          {title} ({items.length})
        </Typography>

        <Button
          variant="outlined"
          size="small"
          onClick={handleArchiveClick}
          disabled={selectedInBucketIds.length === 0}
        >
          Archive
        </Button>
      </Box>

      <Box sx={{ flex: 1, minHeight: 0 }}>
        <SimpleBar style={{ height: "100%" }}>
          <Table
            size="small"
            stickyHeader
            sx={{ tableLayout: "fixed" }}
          >
            <colgroup>
              {COLUMNS.map((col) => (
                <col key={col.id} style={{ width: col.width }} />
              ))}
            </colgroup>

            <TableHead>
              <TableRow>
                {COLUMNS.map((col) => {
                  if (col.id === "select") {
                    return (
                      <TableCell
                        key={col.id}
                        padding="checkbox"
                        sx={{ textAlign: "center" }}
                      >
                        <Checkbox
                          size="small"
                          indeterminate={someSelected}
                          checked={allSelected}
                          onChange={handleHeaderCheckboxChange}
                        />
                      </TableCell>
                    );
                  }

                  return (
                    <TableCell key={col.id} sx={dataCellSx}>
                      {col.label}
                    </TableCell>
                  );
                })}
              </TableRow>
            </TableHead>

            <TableBody>
              {items.map((inst) => {
                const rawDate = inst.next_calibration;
                const parsed = parseISO(rawDate);
                const formatted = isValid(parsed)
                  ? format(parsed, "dd/MM/yyyy")
                  : rawDate;

                const tag = inst.tag?.name || "";
                const client = inst.tag?.location?.site?.client?.name || "";
                const site = inst.tag?.location?.site?.name || "";
                const checked = selectedIds.includes(inst.id);

                return (
                  <TableRow hover key={inst.id}>
                    {COLUMNS.map((col) => {
                      if (col.id === "select") {
                        return (
                          <TableCell
                            key={col.id}
                            padding="checkbox"
                            sx={{ textAlign: "center" }}
                          >
                            <Checkbox
                              size="small"
                              checked={checked}
                              onChange={() => onToggleOne(inst.id)}
                            />
                          </TableCell>
                        );
                      }

                      let value = "";
                      if (col.id === "tag") value = tag;
                      else if (col.id === "client") value = client;
                      else if (col.id === "site") value = site;
                      else if (col.id === "date") value = formatted;

                      return (
                        <TableCell
                          key={col.id}
                          sx={col.id === "tag" ? tagCellClickableSx : dataCellSx}
                          onClick={
                            col.id === "tag"
                              ? () => navigate(APP_PATH.instruments.view(inst.id))
                              : undefined
                          }
                        >
                          {value}
                        </TableCell>
                      );
                    })}
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </SimpleBar>
      </Box>
    </Paper>
  );
};

export default function PageDashboard() {
  const isMountedRef = useIsMountedRef();

  const { data, isLoading, error } = useListInstruments({
    next_calibration__lte: datePlusDays(90),
    archived: false
  });

  const { enqueueSnackbar } = useSnackbar();

  const buckets = useMemo(() => {
    return bucketInstruments(data);
  }, [data])

  const [selectedIds, setSelectedIds] = useState([]);

  const handleToggleOne = (id) => {
    setSelectedIds((prev) =>
      prev.includes(id)
        ? prev.filter((x) => x !== id)
        : [...prev, id]
    );
  };

  const handleToggleMany = (ids) => {
    setSelectedIds((prev) => {
      const allSelected = ids.every((id) => prev.includes(id));
      if (allSelected) {
        // unselect all of these
        return prev.filter((id) => !ids.includes(id));
      }
      // add missing ones
      const set = new Set(prev);
      ids.forEach((id) => set.add(id));
      return Array.from(set);
    });
  };

  const { mutate: archiveInstruments, isLoading: archiveLoading } = useBulkArchiveInstruments();

  const handleArchive = (ids) => {
    archiveInstruments(ids, {
      onSuccess: () => {
        enqueueSnackbar("Instruments Archived!")
      }
    })
  };

  // const onRowClick = (params, event) => {
  //   if (params.row.instrument) {
  //     navigate(APP_PATH.instruments.view(params.row.instrument.id));
  //   } else {
  //     navigate(APP_PATH.instruments.view(params.id));
  //   }
  // };


  return (
    <Page title="Instruments">
      <Container maxWidth="true">
        <HeaderBreadcrumbs
          heading="Dashboard"
          links={[
            { name: '', href: APP_PATH.root },
          ]}
        />

        <Box>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <DueTable
                title="Overdue"
                items={buckets.overdue}
                selectedIds={selectedIds}
                onToggleOne={handleToggleOne}
                onToggleMany={handleToggleMany}
                onArchive={handleArchive}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <DueTable
                title="Due in 1 Week"
                items={buckets.week}
                selectedIds={selectedIds}
                onToggleOne={handleToggleOne}
                onToggleMany={handleToggleMany}
                onArchive={handleArchive}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <DueTable
                title="Due in 1 Month"
                items={buckets.month}
                selectedIds={selectedIds}
                onToggleOne={handleToggleOne}
                onToggleMany={handleToggleMany}
                onArchive={handleArchive}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <DueTable
                title="Due in 3 Months"
                items={buckets.three}
                selectedIds={selectedIds}
                onToggleOne={handleToggleOne}
                onToggleMany={handleToggleMany}
                onArchive={handleArchive}
              />
            </Grid>
          </Grid>
        </Box>

      </Container>
    </Page>
  );
}

